import { Button, ContentfulAboutPageSettings, ContentfulEntry } from '@devtinker/shared';
import React, { useEffect, useState } from 'react';
import { getEntries } from '../lib/contentful';

const About: React.FC = () => {
  const [aboutSettings, setAboutSettings] = useState<ContentfulEntry<ContentfulAboutPageSettings> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const settingsEntries = await getEntries('aboutPageSettings');
        if (settingsEntries.length > 0) {
          setAboutSettings(settingsEntries[0] as any);
        }
      } catch (error) {
        console.error('Error fetching about page data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const header = aboutSettings?.fields.header?.fields;
  const intro = aboutSettings?.fields.introduction?.fields;
  const techStack = aboutSettings?.fields.techStack || [];
  const ctaBox = aboutSettings?.fields.ctaBox?.fields;

  return (
    <div className="max-w-4xl mx-auto space-y-12">
      <div className="text-center">
        <h1 className="text-5xl font-bold text-gray-900 mb-4">
          {header?.title || 'About DevTinker'}
        </h1>
        <p className="text-xl text-gray-600">
          {header?.subtitle || 'Building. Developing. Tinkering.'}
        </p>
      </div>
      
      {intro && (
        <section className="bg-white p-8 rounded-xl shadow-md border border-gray-100">
          <p className="text-lg text-gray-700 leading-relaxed">
            {intro.content}
          </p>
        </section>
      )}

      {techStack.length > 0 && (
        <section className="bg-gray-50 p-8 rounded-lg border border-gray-200">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            {aboutSettings?.fields.techStackTitle || 'Our Technology Stack'}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {techStack.map((tech) => (
              <div key={tech.sys.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <strong className="text-gray-900">{tech.fields.name}</strong>
                <p className="text-gray-600 text-sm mt-1">{tech.fields.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {ctaBox && (
        <section className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
          <h2 className="text-3xl font-bold mb-4 text-gray-900">{ctaBox.title}</h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            {ctaBox.description}
          </p>
          <a 
            href={ctaBox.buttonLink} 
            target={ctaBox.openInNewTab ? "_blank" : undefined}
            rel={ctaBox.openInNewTab ? "noopener noreferrer" : undefined}
          >
            <Button variant="primary" size="lg">{ctaBox.buttonText}</Button>
          </a>
        </section>
      )}
    </div>
  );
};

export default About;
